class CartDountDown extends HTMLElement {
  constructor() {
    super();
    this.minutes = parseInt(this.getAttribute('data-min')) || 10;
    this.storageKey = 'cart-countdown-end-time';
    this.countdownInterval = null;
  }

  connectedCallback() {
    this.initCountdown();
  }

  disconnectedCallback() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  initCountdown() {
    const storedEndTime = localStorage.getItem(this.storageKey);
    const currentTime = Date.now();
    let endTime;

    if (storedEndTime) {
      endTime = parseInt(storedEndTime);
      
      // Check if the countdown has expired
      if (currentTime >= endTime) {
        // Restart countdown
        endTime = currentTime + (this.minutes * 60 * 1000);
        localStorage.setItem(this.storageKey, endTime.toString());
      }
    } else {
      // First time - start new countdown
      endTime = currentTime + (this.minutes * 60 * 1000);
      localStorage.setItem(this.storageKey, endTime.toString());
    }

    this.startCountdown(endTime);
  }

  startCountdown(endTime) {
    this.updateDisplay(endTime);

    this.countdownInterval = setInterval(() => {
      const currentTime = Date.now();
      const timeLeft = endTime - currentTime;

      if (timeLeft <= 0) {
        // Time expired - restart countdown
        const newEndTime = Date.now() + (this.minutes * 60 * 1000);
        localStorage.setItem(this.storageKey, newEndTime.toString());
        this.startCountdown(newEndTime);
        clearInterval(this.countdownInterval);
      } else {
        this.updateDisplay(endTime);
      }
    }, 1000);
  }

  updateDisplay(endTime) {
    const currentTime = Date.now();
    const timeLeft = Math.max(0, endTime - currentTime);

    const minutes = Math.floor(timeLeft / 60000);
    const seconds = Math.floor((timeLeft % 60000) / 1000);

    this.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
}

customElements.define('cart-dount-down', CartDountDown);

class ShippingProgressBar extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    
    // Default properties
    this._threshold = 7500; // $75.00 in cents
    this._current = 0;
    this._currency = '$';
    this._thresholdMessage = 'Add __AMOUNT__ more to get FREE shipping!';
    this._successMessage = 'Congratulations! You qualify for FREE shipping!';
    this._progressColor = '#4CAF50';
    this._backgroundColor = '#f0f0f0';
  }

  static get observedAttributes() {
    return [
      'threshold', 
      'current', 
      'currency', 
      'threshold-message', 
      'success-message',
      'progress-color',
      'background-color'
    ];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    
    switch(name) {
      case 'threshold':
        this._threshold = parseInt(newValue, 10);
        break;
      case 'current':
        this._current = parseInt(newValue, 10);
        break;
      case 'currency':
        this._currency = newValue;
        break;
      case 'threshold-message':
        this._thresholdMessage = newValue;
        break;
      case 'success-message':
        this._successMessage = newValue;
        break;
      case 'progress-color':
        this._progressColor = newValue;
        break;
      case 'background-color':
        this._backgroundColor = newValue;
        break;
    }
    
    this.render();
  }

  connectedCallback() {
    // Get attributes on initial render
    if (this.hasAttribute('threshold')) {
      this._threshold = parseInt(this.getAttribute('threshold'), 10);
    }
    
    if (this.hasAttribute('current')) {
      this._current = parseInt(this.getAttribute('current'), 10);
    }
    
    if (this.hasAttribute('currency')) {
      this._currency = this.getAttribute('currency');
    }
    
    if (this.hasAttribute('threshold-message')) {
      this._thresholdMessage = this.getAttribute('threshold-message');
    }
    
    if (this.hasAttribute('success-message')) {
      this._successMessage = this.getAttribute('success-message');
    }
    
    if (this.hasAttribute('progress-color')) {
      this._progressColor = this.getAttribute('progress-color');
    }
    
    if (this.hasAttribute('background-color')) {
      this._backgroundColor = this.getAttribute('background-color');
    }
    
    this.render();
  }

  formatCurrency(cents) {
    return this._currency + (cents / 100).toFixed(0);
  }

  render() {
    // Calculate progress bar values
    const progressPercentage = Math.min(100, (this._current / this._threshold) * 100);
    const remainingAmount = Math.max(0, this._threshold - this._current);
    const formattedAmount = this.formatCurrency(remainingAmount);
    const formattedStartAmount = this.formatCurrency(0);
    const formattedEndAmount = this.formatCurrency(this._threshold);
    // Create HTML content
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          width: 100%;
        }
        
        .progress-container {
          margin: 0;
          padding: 0;
          border-radius: 0px;
          background-color: white;
          margin-bottom:0px;
        }
        
        .progress-message {
          margin-bottom: 5px;
          text-align: left;
          font-size: 14px;
          line-height: 120%;
        }
        
        
        .progress-bar {
          height: 5px;
          background-color: ${this._backgroundColor};
          border-radius: 21px;
          overflow: hidden;
          position: relative;
        }
        
        .progress-fill {
          height: 100%;
          width: ${progressPercentage}%;
          background-color: ${this._progressColor};
          border-radius: 5px;
          transition: width 0.4s ease-in-out;
        }
        .progress-remaining{
          font-weight:600;
        }
        .custom-bottom-amount {
            display:  ${progressPercentage < 100 ? 'flex' : 'none'};
            align-items: center;
            justify-content: space-between;
            width: 100%;
        }
      </style>
      
      <div class="progress-container">
        <div class="progress-message">
          ${remainingAmount > 0 
            ? this._thresholdMessage.replace('__AMOUNT__', `<span class="progress-remaining">${formattedAmount}</span>`)
            : `<span class="progress-complete">${this._successMessage}</span>`
          }
        </div>
        <div class="progress-bar">
          <div class="progress-fill"></div>
        </div>
        <div class="custom-bottom-amount">
          <span>${formattedStartAmount}</span>
          <span>${formattedEndAmount}</span>
        </div>
      </div>
    `;
  }
}


// Register the custom element
customElements.define('shipping-progress-bar', ShippingProgressBar);
class CustomCartMainUpsell extends HTMLElement {
    constructor() {
      super();
      this.buttons = this.querySelectorAll(".button");
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.swiperElement = this.querySelector('.custom-main-upsell-swiper-slider');
      this.mainId = this.dataset.pid;
      
    }
    async init(){
    
      const cartRequest = await fetch('/cart.js');
      const cartData = await cartRequest.json();
      const matchProducts = cartData.items.filter(item => item.product_id == this.mainId);
      const totalQuantity = matchProducts.reduce((sum, item) => sum + (item.quantity || 0), 0);

      if(matchProducts.length > 0 && totalQuantity == cartData.item_count){
        let updates = matchProducts.reduce((acc, { key }) => {
            acc[key] = 0;
            return acc;
        }, {});
        this.removeAutoUpsell(updates);
      }
      if(this.buttons.length > 0){
        this.style.display="block";
      }else{
        this.style.display="none";
      }
      this.loadSwiperSlider();
    }
    removeAutoUpsell(updates){
      console.log("hhhh");
       const body = JSON.stringify({
        updates,
        sections:["cart-drawer", "cart-icon-bubble"],
        sections_url: window.location.pathname,
      });
      fetch('/cart/update.js',{
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: body
      })
        .then((response) => {
          return response.json();
        })
        .then((response) => {
              CartPerformance.measure("remove:paint-updated-sections", () => {
              this.cart.renderContents(response);
            });
             this.cart.classList.toggle('is-empty', response.item_count === 0);
        })
        .catch(() => {
        })
        .finally(() => {
        });
    }
    connectedCallback() {
      this.init();
      this.setupEventListeners();
    }
    
    disconnectedCallback() {
    }
    setupEventListeners() {
      //this.buttons.addEventListener("click",this.upsellAddToCart.bind(this));
      this.buttons.forEach((el) => {
        el.addEventListener("click",this.upsellAddToCart.bind(this));
      })
    }
    loadSwiperSlider(){
      this.paginationElement = this.querySelector('.swiper-pagination');
      const swiper = new Swiper(this.swiperElement, {
        slidesPerView: 1,
        spaceBetween: 10,
        centeredSlides: false,
        pagination: {
          el: '.swiper-pagination',
          clickable: true,
        }
      });
    }
    upsellAddToCart(evt){
      evt.preventDefault();
      const button = evt.target;
      const mainId = evt.target.dataset.vid;
      let items = [
          {
            id:mainId,
            quantity:1,
          }
      ];
      const formData = {
        items:items,
        sections:this.cart.getSectionsToRender().map((section) => section.id),
        sections_url:window.location.pathname
      };
      button.classList.add('loading');
      button.querySelector('.loading__spinner').classList.remove('hidden');
      fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        })
        .then(response => {
          return response.json();
        })
        .then((response) => {
          if (response.status) {
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }
          }
          const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: mainId,
            cartData: response,
          }).then(() => {
            CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
          });      
          CartPerformance.measure("add:paint-updated-sections", () => {
            this.cart.renderContents(response);
          });
              
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          button.classList.remove('loading');
          button.querySelector('.loading__spinner').classList.add('hidden');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          CartPerformance.measureFromEvent("add:user-action", evt);
        });
    }
    
}

// Register the custom element
customElements.define('custom-cart-main-upsell', CustomCartMainUpsell);

document.addEventListener("DOMContentLoaded", (event) => {
  initSubscriptionEvents();
  document.querySelectorAll('.custom-anchor-point label').forEach(label => {
    label.addEventListener('click', function() {
      // Get the target ID from the data-target attribute
      const targetId = this.getAttribute('data-target');
      
      // Find the target element using the ID
      const targetElement = document.getElementById(targetId);
      
      // If target element exists, scroll to it with smooth behavior
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    });
  });
  document.querySelectorAll('button[data-traget="custom-plan-main-selector"]').forEach((button)=> {
    button.addEventListener('click', function() {
        const targetId = button.getAttribute('data-traget');
        const targetElement = document.getElementById(targetId);
        
        if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth' });
        }
    });
  })
  document.querySelectorAll('.custom-scrolling[data-traget="custom-plan-main-selector"]').forEach((button)=> {
    button.addEventListener('click', function() {
        const targetId = button.getAttribute('data-traget');
        const targetElement = document.getElementById(targetId);
        
        if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth' });
        }
    });
  })
  
});
subscribe(PUB_SUB_EVENTS.variantChange,(evt) => {
  initSubscriptionEvents();
});
function initSubscriptionEvents(){
const subscriptionTypeSelectors = document.querySelectorAll(".subscription-type-selector-btn");
  const sellingPlanSelectors = document.querySelectorAll(".selling-plan-delivery-selector");
  const deliveryMainWrapper = document.querySelector(".custom-delivery-option-selector");

  if(subscriptionTypeSelectors.length > 0){
    subscriptionTypeSelectors.forEach((button) => {
      button.addEventListener("click",(evt) => {
        subscriptionTypeSelectors.forEach(button => button.classList.remove("active"));
        button.classList.add("active");
        const selling_plan_input = document.querySelector('[data-selling-plan-input]');
        evt.preventDefault();
        if(button.dataset.type == 'subscribe' && deliveryMainWrapper){
          deliveryMainWrapper.classList.remove("hidden");
          const selectedSwatchElement = document.querySelector(".custom-variant-selector-wrapper .pouch-option.selected input");
          if(selectedSwatchElement){
            const selling_id = selectedSwatchElement.dataset.selling;
            if(document.querySelectorAll(`.selling-plan-delivery-selector.active`).length <= 0){
               document.querySelector(`.selling-plan-delivery-selector[data-id="${selling_id}"]`).dispatchEvent(new Event("click"));
            }
           
          }
          document.querySelectorAll(".onetime").forEach(el => el.classList.add("hidden"));
          document.querySelectorAll(".subscribe").forEach(el => el.classList.remove("hidden"));
        }else{
          document.querySelectorAll(".onetime").forEach(el => el.classList.remove("hidden"));
          document.querySelectorAll(".subscribe").forEach(el => el.classList.add("hidden"));
          selling_plan_input.value ="";
          updateSellingPlan("");
          deliveryMainWrapper.classList.add("hidden");
        }
      });
    });
  }
   if(sellingPlanSelectors.length > 0){
      sellingPlanSelectors.forEach((button) => {
        button.addEventListener("click",(evt) => {
           sellingPlanSelectors.forEach(button => button.classList.remove("active"));
           button.classList.add("active");
          const selling_plan_input = document.querySelector('[data-selling-plan-input]');
          evt.preventDefault();
          selling_plan_input.value = button.dataset.id;
          updateSellingPlan(button.dataset.id);
        });
      });
   }
   const selectedVariantInput = document.querySelector(".custom-variant-selector-wrapper input:checked");
   const selectedOptionType = document.querySelector(".subscription-type-selector-btn.active");
   if(selectedVariantInput && selectedOptionType.dataset.type == 'subscribe'){
    document.querySelector(`.selling-plan-delivery-selector[data-id="${selectedVariantInput.dataset.selling}"]`).click();
   
   }
}
function updateSellingPlan(sellingPlanId) {

    const productInfo = document.querySelector('product-info');
    productInfo.dataset.sellingPlan = sellingPlanId;
  // Update URL
  const variantId = getCurrentVariantId();
  updateURL(variantId, sellingPlanId);

  // Dispatch custom event
  document.dispatchEvent(new CustomEvent('subscription:changed', {
    detail: { 
      sellingPlanId, 
      variantId
    }
  }));
}
function updateURL(variantId, sellingPlanId) {
   const productInfo = document.querySelector('product-info');
  const url = new URL(window.location);
  
  if (variantId) {
    url.searchParams.set('variant', variantId);
  }
  
  if (sellingPlanId) {
    url.searchParams.set('selling_plan', sellingPlanId);
  } else {
    url.searchParams.delete('selling_plan');
  }
  
  window.history.replaceState({}, '', url);

  // Update share button if exists
  const shareButton = productInfo?.querySelector('share-button');
  shareButton?.updateUrl(url.toString());
}
function getCurrentVariantId() {
  const productInfo = document.querySelector('product-info');
  const variantInput = productInfo?.querySelector('input[name="id"]');
  return variantInput?.value || null;
}